import React, { useEffect } from "react";
import { Col, Row, Select } from "antd";
import { Controller } from "react-hook-form";
import CustomTextfield from "../../../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../../../common/input/custom-select/custom-select";
import { GlobalOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { Country, State } from "country-state-city";
import CustomAutoComplete from "../../../../../../common/input/custom-autocomplete/custom-autocomplete";

const ContactInformation = ({
  control,
  getValues,
  resetField,
  setValue,
  watch,
  trigger
}) => {
  const physicalAddress = watch("physical_address");
  const lat = watch("lat");
  const lng = watch("lng");

  useEffect(() => {
    if (physicalAddress) {
      trigger("physical_address");
    }
  }, [lat, lng]);

  return (
    <Row gutter={[32, 20]}>
      <Col span={24}>
        <Controller
          name="physical_address"
          control={control}
          rules={{
            required: "Physical Address is required",
            validate: () => {
              if (!lat || !lng) {
                return "Please select a valid address from the dropdown";
              }
              return true;
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomAutoComplete
              label="Physical Address"
              showRequired
              id="physical_address"
              placeholder="Enter address"
              value={value}
              onChange={(val) => {
                onChange(val);
                setValue("lat", "");
                setValue("lng", "");
              }}
              onSelect={(d) => {
                setValue("lat", d.lat);
                setValue("lng", d.lng);
              }}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={24}>
        <Controller
          name="postal_address"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="postal_address"
              label="Postal Address"
              showOptional="(if different)"
              labelColor="#171717"
              placeholder="Type School name"
              size="large"
              value={value}
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="country"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomSelect
              id="country"
              label="Country"
              showRequired
              labelColor="#171717"
              placeholder="--Select--"
              showSearch={true}
              size="large"
              value={value}
              onChange={(...d) => {
                resetField("province");
                onChange(...d);
              }}
              status={error ? "error" : ""}
            >
              {Country.getAllCountries().map((item, index) => (
                <Select.Option key={index} value={item?.name}>
                  {item?.name}
                </Select.Option>
              ))}
            </CustomSelect>
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="province"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomSelect
              id="province"
              label="Province"
              showRequired
              labelColor="#171717"
              placeholder="--Select--"
              showSearch={true}
              size="large"
              value={value}
              onChange={onChange}
              status={error ? "error" : ""}
            >
              {State.getStatesOfCountry(
                Country.getAllCountries().find(
                  (i) => i.name === getValues("country")
                )?.isoCode
              ).map((item, index) => (
                <Select.Option key={index} value={item?.name}>
                  {item?.name}
                </Select.Option>
              ))}
            </CustomSelect>
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="principal_phone_number"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="principal_phone_number"
              label="Head/Principal's Phone"
              showRequired
              labelColor="#171717"
              placeholder="Enter Phone Number"
              type="number"
              size="large"
              value={value}
              prefix={
                <PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="principal_email_address"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="principal_email_address"
              label="Email of Head/Principal"
              showOptional
              type="email"
              labelColor="#171717"
              placeholder="Enter Email"
              size="large"
              value={value}
              prefix={
                <MailOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="deputy_principal_phone_number"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="deputy_principal_phone_number"
              label="Phone of Deputy Head/Principal"
              showOptional
              labelColor="#171717"
              placeholder="Enter Phone Number"
              type="number"
              size="large"
              value={value}
              prefix={
                <PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="deputy_principal_email_address"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="deputy_principal_email_address"
              label="Email of Deputy Head/Principal"
              showOptional
              type="email"
              labelColor="#171717"
              placeholder="Enter Email"
              size="large"
              value={value}
              prefix={
                <MailOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="school_phone_number"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="school_phone_number"
              label="School Phone Number"
              showRequired
              labelColor="#171717"
              placeholder="Enter Phone Number"
              type="number"
              size="large"
              value={value}
              prefix={
                <PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="school_email"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="school_email"
              label="School Email"
              showOptional
              type="email"
              labelColor="#171717"
              placeholder="Enter Email"
              size="large"
              value={value}
              prefix={
                <MailOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col span={12}>
        <Controller
          name="school_website_url"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomTextfield
              id="school_website_url"
              label="Website URL"
              showOptional
              type="url"
              labelColor="#171717"
              placeholder="Enter website URL"
              size="large"
              value={value}
              prefix={
                <GlobalOutlined style={{ color: "#999999", paddingRight: 4 }} />
              }
              onChange={onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>
    </Row>
  );
};

export default ContactInformation;
